import { useState, useContext, useEffect } from "react";
import { Formik, Form } from "formik";
import { TabView, TabPanel } from "primereact/tabview";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DetailsAds, ImagesAds } from "../../components";
import GoBack from "../../components/GoBack/GoBack";
import Layout from "../Layout/Layout";
import { UserContext } from "../../components/Context/AuthUser";
import { createAd, updateAd, getAdById, deleteAd } from "../../api/ads.api.js";
import { getAllOwners } from "../../api/contacts.api";
import { getAllConsultants } from "../../api/consultants.api";
import Spinner from "../../components/Spinner/Spinner";
import { FiSave } from "react-icons/fi";
import { FaTrash } from "react-icons/fa";
import { HiDuplicate } from "react-icons/hi";
import { GiCancel } from "react-icons/gi";

import { AdsMatching } from "../../components/AdsMatching/AdsMatching";

import {
  getAllResidentialZones,
  getAllPatrimonialZones,
  getAllOthersZones,
  getAllCostaZones,
  getAllRusticAndSingularZones,
} from "../../api/zones.api";
import useWindowSize from "../../hooks/useWindowSize";
import {
  departmentUrl,
  getPropertyUrl,
} from "./../../functions/ads/ad-functions";
import "./AdForm.scss";
import "./EmailTemplate.scss";
import AdsHistory from "../../components/AdsHistory/AdsHistory";
import { WebImage } from "../../icons";

const AdForm = () => {
  const { id } = useParams();
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const idCopy = urlParams.get("idCopy");
  const { user } = useContext(UserContext);
  const size = useWindowSize();

  const [adById, setAdById] = useState("");
  const [selectedOwner, setSelectedOwner] = useState("");
  const [selectedConsultant, setSelectedConsultant] = useState(user._id);
  const [residentialSelectedZones, setResidentialSelectedZones] = useState([]);
  const [patrimonialSelectedZones, setPatrimonialSelectedZones] = useState([]);
  const [costaSelectedZones, setCostaSelectedZones] = useState([]);
  const [rusticAndSingularSelectedZones, setRusticAndSingularSelectedZones] =
    useState([]);
  const [othersSelectedZones, setOthersSelectedZones] = useState([]);
  const [selectedAdBuildingType, setSelectedAdBuildingType] = useState([]);
  const [selectedRealStatePortals, setSelectedRealStatePortals] = useState([]);
  const [selectedAdType, setSelectedAdType] = useState([]);
  const [residentials, setResidential] = useState([]);
  const [patrimonials, setPatrimonial] = useState([]);
  const [costa, setCosta] = useState([]);
  const [rusticAndSingular, setRusticAndSingular] = useState([]);
  const [others, setOthers] = useState([]);
  const [department, setDepartment] = useState("");
  const [adStatus, setAdStatus] = useState("En preparación");
  const [adShowOnWeb, setAdShowOnWeb] = useState(true);
  const [adOffMarket, setAdOffMarket] = useState(false);
  const [validateForm, setValidateForm] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [owners, setOwners] = useState([]);
  const [consultants, setConsultants] = useState([]);

  const [disabledButtons, setDisabledButtons] = useState(false);
  const [updateSurfacesBox, setUpdateSurfacesBox] = useState(true);

  const [activeIndex, setActiveIndex] = useState(0);

  const [loader, setLoader] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      // loader false significa "cargando"
      setLoader(false);

      try {
        // Lanzamos todas las peticiones de catálogos en PARALELO.
        const [
          resResid,
          resPatri,
          resOther,
          resCosta,
          resRusticAndSingular,
          resOwners,
          resConsultants,
        ] = await Promise.all([
          getAllResidentialZones(),
          getAllPatrimonialZones(),
          getAllOthersZones(),
          getAllCostaZones(),
          getAllRusticAndSingularZones(),
          getAllOwners(),
          getAllConsultants(),
        ]);

        setResidential(resResid);
        setPatrimonial(resPatri);
        setOthers(resOther);
        setCosta(resCosta);
        setRusticAndSingular(resRusticAndSingular);
        setOwners(resOwners);
        setConsultants(resConsultants);

        // Si hay ID o idCopy, traemos el anuncio específico ANTES de apagar el loader.
        const targetId = id || idCopy;
        if (targetId) {
          const ad = await getAdById(targetId);

          setAdById(ad);
          setAdShowOnWeb(ad.showOnWeb);
          setAdOffMarket(ad.showOnWebOffMarket);
          setSelectedOwner(ad.owner);
          setSelectedConsultant(ad.consultant);
          setAdStatus(ad.adStatus);
          setSelectedRealStatePortals(ad.realStatePortals);
          setSelectedAdBuildingType(ad.adBuildingType);
          setSelectedAdType(ad.adType);
        }

        // Cambiamos el loader a true (true parece ser "ocultar spinner")
        setLoader(true);
      } catch (error) {
        console.error("Error cargando los datos del formulario:", error);
        // En caso de error, también deberías permitir que el usuario vea la UI o un mensaje de error
        setLoader(true);
      }
    };

    fetchData();
  }, [id, idCopy]);
  const targetId = id || idCopy; // Definimos esto para usarlo en el if

  if (
    firstLoad &&
    targetId &&
    adById.length !== 0 &&
    residentials.length !== 0 &&
    patrimonials.length !== 0 &&
    others.length !== 0 &&
    costa.length !== 0 &&
    validateForm === false
  ) {
    // 1. Extraemos los IDs puros del anuncio guardado
    const savedZoneIds = adById.zone.map((z) =>
      typeof z === "object" ? z._id : z,
    );

    for (let zone of residentials) {
      if (
        savedZoneIds.includes(zone._id) &&
        !residentialSelectedZones.includes(zone._id)
      ) {
        residentialSelectedZones.push(zone._id);
      }
    }
    for (let zone of patrimonials) {
      if (
        savedZoneIds.includes(zone._id) &&
        !patrimonialSelectedZones.includes(zone._id)
      ) {
        patrimonialSelectedZones.push(zone._id);
      }
    }
    for (let zone of others) {
      if (
        savedZoneIds.includes(zone._id) &&
        !othersSelectedZones.includes(zone._id)
      ) {
        othersSelectedZones.push(zone._id);
      }
    }
    for (let zone of costa) {
      if (
        savedZoneIds.includes(zone._id) &&
        !costaSelectedZones.includes(zone._id)
      ) {
        costaSelectedZones.push(zone._id);
      }
    }
    for (let zone of rusticAndSingular) {
      if (
        savedZoneIds.includes(zone._id) &&
        !rusticAndSingularSelectedZones.includes(zone._id)
      ) {
        rusticAndSingularSelectedZones.push(zone._id);
      }
    }

    setFirstLoad(false);
  }

  useEffect(() => {
    if (user.length === 0) {
      navigate("/");
    }
  }, [user, navigate]);

  console.log(adById);

  return (
    <>
      <div>
        <Layout
          subTitle="Anuncios"
          subUndertitle={
            <div className="adForm__subHeader__options">
              <div className="adForm__subHeader__options__firstBlock">
                <GoBack />
                {size < 478 && loader && (
                  <a
                    href={
                      id && adById
                        ? `https://gvre.es${getPropertyUrl({
                            category: adById.department,
                            location: {
                              city: adById.adDirection?.city || "Madrid",
                            },
                            salePrice:
                              adById.sale?.saleValue > 0
                                ? adById.sale.saleValue
                                : null,
                            slug: adById.slug || id,
                          })}`
                        : null
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="adForm__filter-box--item__active"
                  >
                    <WebImage
                      className="navigate__link--icon"
                      style={{ width: "2em", height: "2em" }}
                    />
                    Ir a web
                  </a>
                )}
              </div>
              <div className="adForm__filter-box__container">
                <div className="adForm__filter-box">
                  {activeIndex === 0 && (
                    <>
                      <button
                        className={
                          adStatus === "En preparación"
                            ? "adForm__filter-box--item__active"
                            : "adForm__filter-box--item"
                        }
                        onClick={() => {
                          setAdStatus("En preparación");
                          setActiveIndex(0);
                        }}
                      >
                        En preparación
                      </button>
                      <button
                        className={
                          adStatus === "Activo"
                            ? "adForm__filter-box--item__active"
                            : "adForm__filter-box--item"
                        }
                        onClick={() => {
                          setAdStatus("Activo");
                        }}
                      >
                        <p>Activo</p>
                      </button>
                      <button
                        className={
                          adStatus === "Inactivo"
                            ? "adForm__filter-box--item__active"
                            : "adForm__filter-box--item"
                        }
                        onClick={() => {
                          setAdStatus("Inactivo");
                          setAdShowOnWeb(false);
                          setAdOffMarket(false);
                          setActiveIndex(0);
                        }}
                      >
                        <p>Inactivo</p>
                      </button>
                    </>
                  )}
                </div>
                {size > 478 && loader ? (
                  <>
                    <a
                      href={
                        id && adById
                          ? `https://gvre.es${getPropertyUrl({
                              category: adById.department,
                              location: {
                                city: adById.adDirection?.city || "Madrid",
                              },
                              salePrice:
                                adById.sale?.saleValue > 0
                                  ? adById.sale.saleValue
                                  : null,
                              slug: adById.slug || id,
                            })}`
                          : null
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="adForm__filter-box--item__active"
                    >
                      <WebImage
                        className="navigate__link--icon"
                        style={{ width: "2em", height: "2em" }}
                      />
                      Ir a web
                    </a>
                  </>
                ) : null}
              </div>
            </div>
          }
          subLocation="/anuncios/crear"
          subBreadcrumbs={
            id
              ? loader
                ? `Anuncio ${adById.adReference}`
                : "Cargando anuncio..."
              : "Crear nuevo anuncio"
          }
          footContent={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2rem",
                justifyContent: "center",
              }}
            >
              {disabledButtons ? (
                <>
                  {activeIndex === 0 && (
                    <button
                      className="buttondisabled"
                      type="submit"
                      form="AdForm"
                    >
                      <FiSave size={15} />
                      {size > 495 && "Guardar"}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {activeIndex === 0 && (
                    <button
                      className="buttonNormalActive"
                      type="submit"
                      form="AdForm"
                    >
                      <FiSave size={15} />
                      {size > 495 && "Guardar"}
                    </button>
                  )}
                </>
              )}
              {disabledButtons ? (
                <>
                  {activeIndex === 0 && id && (
                    <button className="buttonDisabled">
                      <HiDuplicate size={17} />

                      {size > 495 && "Duplicar"}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {activeIndex === 0 && id && (
                    <a
                      href={`/anuncios/crear?idCopy=${id}`}
                      className="buttonNormal"
                    >
                      <HiDuplicate size={17} />
                      {size > 495 && "Duplicar"}
                    </a>
                  )}
                </>
              )}
              {disabledButtons ? (
                <>
                  <button className="buttonDisabled ">
                    <GiCancel size={15} color="black" />
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <Link className="buttonNormal" to="/anuncios">
                    <GiCancel size={15} color="black" />
                    Cancelar
                  </Link>
                </>
              )}
              {disabledButtons ? (
                <>
                  {id && user.role === "Admin" && (
                    <button
                      className="buttonDeleteDisabled"
                      onClick={() => {
                        // Añadimos el window.confirm aquí
                        const confirmDelete = window.confirm(
                          "¿Estás seguro de que deseas borrar este anuncio? Esta acción no se puede deshacer.",
                        );
                        if (confirmDelete) {
                          setDisabledButtons(true);
                          deleteAd(id).then(() => {
                            alert("Anuncio borrado correctamente");
                            navigate("/anuncios");
                          });
                        }
                      }}
                    >
                      <FaTrash size={12} color="#f4f4f4" />
                      {size > 495 && "Borrar"}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {id && user.role === "Admin" && (
                    <button
                      className="buttonDelete"
                      onClick={() => {
                        // Añadimos el window.confirm aquí
                        const confirmDelete = window.confirm(
                          "¿Estás seguro de que deseas borrar este anuncio? Esta acción no se puede deshacer.",
                        );
                        if (confirmDelete) {
                          setDisabledButtons(true);
                          deleteAd(id).then(() => {
                            alert("Anuncio borrado correctamente");
                            navigate("/anuncios");
                          });
                        }
                      }}
                    >
                      <FaTrash size={12} color="#f4f4f4" />
                      {size > 495 && "Borrar"}
                    </button>
                  )}
                </>
              )}
            </div>
          }
        >
          {owners.length <= 0 &&
          residentialSelectedZones.length !== 0 &&
          patrimonialSelectedZones.length !== 0 &&
          othersSelectedZones.length &&
          costaSelectedZones.length &&
          loader ? (
            <Spinner />
          ) : (
            <TabView
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
            >
              <TabPanel header="Detalles">
                {loader ? (
                  <Formik
                    enableReinitialize={true}
                    initialValues={{
                      updateSameRef: updateSurfacesBox,
                      title: adById ? adById.title : "",
                      adReference: adById ? adById.adReference : "",
                      internalComments: adById ? adById.internalComments : "",
                      adStatus: adById ? adById.adStatus : "En preparación",
                      showOnWeb: adById ? adById.showOnWeb : true,
                      featuredOnMain: adById ? adById.featuredOnMain : false,
                      featuredDrawings: adById
                        ? adById.featuredDrawings
                        : false,
                      street: adById ? adById.adDirection.address.street : "",
                      directionNumber: adById
                        ? adById.adDirection.address.directionNumber
                        : "",
                      directionFloor: adById
                        ? adById.adDirection.address.directionFloor
                        : "",
                      postalCode: adById ? adById.adDirection.postalCode : "",
                      city: adById ? adById.adDirection.city : "Madrid",
                      country: adById ? adById.adDirection.country : "España",
                      adType: adById ? adById.adType : [],
                      gvOperationClose: adById ? adById.gvOperationClose : "",
                      owner: adById ? [adById.owner] : "",
                      consultant: adById ? [adById.consultant] : "",
                      realStatePortals: adById ? adById.realStatePortals : [],
                      adBuildingType: adById ? adById.adBuildingType : [],
                      zone: adById ? adById.zone : [],
                      department: adById ? adById.department : "",
                      webSubtitle: adById ? adById.webSubtitle : "",
                      buildSurface: adById ? adById.buildSurface : 0,
                      plotSurface: adById ? adById.plotSurface : 0,
                      m2StorageSpace: adById ? adById.m2StorageSpace : 0,
                      m2Terrace: adById ? adById.m2Terrace : 0,
                      floor: adById ? adById.floor : "",
                      disponibility: adById ? adById.disponibility : "",
                      profitability: adById ? adById.profitability : false,
                      profitabilityValue: adById
                        ? adById.profitabilityValue
                        : "",
                      surfacesBox: adById ? adById.surfacesBox : [],

                      saleValue: adById.sale ? adById.sale.saleValue : 0,
                      saleShowOnWeb: adById.sale
                        ? adById.sale.saleShowOnWeb
                        : true,
                      saleRepercussionM2: adById.sale
                        ? adById.sale.saleRepercussionM2
                        : true,
                      saleRepercussionM2ShowOnWeb: adById.sale
                        ? adById.sale.saleRepercussionM2ShowOnWeb
                        : true,

                      rentValue: adById.rent ? adById.rent.rentValue : 0,
                      rentShowOnWeb: adById.rent
                        ? adById.rent.rentShowOnWeb
                        : true,

                      monthlyRent: adById ? adById.monthlyRent : 0,
                      expenses: adById ? adById.expenses : 0,
                      expensesIncluded: adById ? adById.expensesIncluded : 0,
                      expensesValue: adById
                        ? adById.communityExpenses.expensesValue
                        : 0,
                      expensesShowOnWeb: adById
                        ? adById.communityExpenses.expensesShowOnWeb
                        : false,
                      ibiValue: adById ? adById.ibi.ibiValue : 0,
                      ibiShowOnWeb: adById ? adById.ibi.ibiShowOnWeb : false,
                      trashFeeValue: adById
                        ? adById.trashFee?.trashFeeValue
                        : 0,
                      trashFeeShowOnWeb: adById
                        ? adById.trashFee?.trashFeeShowOnWeb
                        : false,
                      buildingYear: adById ? adById.buildingYear : "",
                      bedrooms: adById ? adById.quality.bedrooms : 0,
                      bathrooms: adById ? adById.quality.bathrooms : 0,
                      parking: adById ? adById.quality.parking : 0,
                      reformed: adById ? adById.quality.reformed : false,
                      toReform: adById ? adById.quality.toReform : false,
                      indoorPool: adById ? adById.quality.indoorPool : 0,
                      outdoorPool: adById ? adById.quality.outdoorPool : 0,
                      jobPositions: adById ? adById.quality.jobPositions : 0,
                      subway: adById ? adById.quality.subway : "",
                      bus: adById ? adById.quality.bus : "",
                      storageRoom: adById ? adById.quality.storageRoom : "",
                      lift: adById ? adById.quality.others.lift : false,
                      dumbwaiter: adById
                        ? adById.quality.others.dumbwaiter
                        : false,
                      liftTruck: adById
                        ? adById.quality.others.liftTruck
                        : false,
                      airConditioning: adById
                        ? adById.quality.others.airConditioning
                        : false,
                      centralHeating: adById
                        ? adById.quality.others.centralHeating
                        : false,
                      subfloorHeating: adById
                        ? adById.quality.others.subfloorHeating
                        : false,
                      indoorAlarm: adById
                        ? adById.quality.others.indoorAlarm
                        : false,
                      outdoorAlarm: adById
                        ? adById.quality.others.outdoorAlarm
                        : false,
                      fullHoursSecurity: adById
                        ? adById.quality.others.fullHoursSecurity
                        : false,
                      gunRack: adById ? adById.quality.others.gunRack : false,
                      strongBox: adById
                        ? adById.quality.others.strongBox
                        : false,
                      well: adById ? adById.quality.others.well : false,
                      homeAutomation: adById
                        ? adById.quality.others.homeAutomation
                        : false,
                      centralVacuum: adById
                        ? adById.quality.others.centralVacuum
                        : false,
                      padelCourt: adById
                        ? adById.quality.others.padelCourt
                        : false,
                      tennisCourt: adById
                        ? adById.quality.others.tennisCourt
                        : false,
                      terrace: adById ? adById.quality.others.terrace : false,
                      storage: adById ? adById.quality.others.storage : false,
                      swimmingPool: adById
                        ? adById.quality.others.swimmingPool
                        : false,
                      indoorPoolCheck: adById
                        ? adById.quality.others.indoorPoolCheck
                        : false,
                      outdoorPoolCheck: adById
                        ? adById.quality.others.outdoorPoolCheck
                        : false,
                      outdoorPoolClimatized: adById
                        ? adById.quality.others.outdoorPoolClimatized
                        : false,
                      garage: adById ? adById.quality.others.garage : false,
                      falseCeiling: adById
                        ? adById.quality.others.falseCeiling
                        : false,
                      raisedFloor: adById
                        ? adById.quality.others.raisedFloor
                        : false,
                      qualityBathrooms: adById
                        ? adById.quality.others.qualityBathrooms
                        : false,
                      freeHeight: adById
                        ? adById.quality.others.freeHeight
                        : false,
                      smokeOutlet: adById
                        ? adById.quality.others.smokeOutlet
                        : false,
                      accessControl: adById
                        ? adById.quality.others.accessControl
                        : false,
                      furnished: adById
                        ? adById.quality.others.furnished
                        : false,
                      implanted: adById
                        ? adById.quality.others.implanted
                        : false,
                      separateEntrance: adById
                        ? adById.quality.others.separateEntrance
                        : false,
                      exclusiveOfficeBuilding: adById
                        ? adById.quality.others.exclusiveOfficeBuilding
                        : false,
                      classicBuilding: adById
                        ? adById.quality.others.classicBuilding
                        : false,
                      coworking: adById
                        ? adById.quality.others.coworking
                        : false,
                      individualHeating: adById
                        ? adById.quality.others.individualHeating
                        : false,
                      concierge: adById
                        ? adById.quality.others.concierge
                        : false,
                      mixedBuilding: adById
                        ? adById.quality.others.mixedBuilding
                        : false,
                      accessiblePMR: adById
                        ? adById.quality.others.accessiblePMR
                        : false,
                      agricultural: adById
                        ? adById.quality.others.agricultural
                        : false,
                      hunting: adById ? adById.quality.others.hunting : false,
                      forestry: adById ? adById.quality.others.forestry : false,
                      livestock: adById
                        ? adById.quality.others.livestock
                        : false,
                      rusticOther: adById
                        ? adById.quality.others.rusticOther
                        : false,
                      warehouses: adById
                        ? adById.quality.others.warehouses
                        : false,
                      secondaryHousing: adById
                        ? adById.quality.others.secondaryHousing
                        : false,
                      equestrianFacilities: adById
                        ? adById.quality.others.equestrianFacilities
                        : false,
                      electricSupply: adById
                        ? adById.quality.others.electricSupply
                        : false,
                      pond: adById ? adById.quality.others.pond : false,
                      reservoir: adById
                        ? adById.quality.others.reservoir
                        : false,

                      recess: adById ? adById.quality.others.recess : false,
                      fenced: adById ? adById.quality.others.fenced : false,
                      porch: adById ? adById.quality.others.porch : false,
                      fireplace: adById
                        ? adById.quality.others.fireplace
                        : false,
                      gym: adById ? adById.quality.others.gym : false,
                      modernStyle: adById
                        ? adById.quality.others.modernStyle
                        : false,
                      classicStyle: adById
                        ? adById.quality.others.classicStyle
                        : false,
                      andaluzStyle: adById
                        ? adById.quality.others.andaluzStyle
                        : false,
                      seaViews: adById ? adById.quality.others.seaViews : false,
                      panoramicView: adById
                        ? adById.quality.others.panoramicView
                        : false,
                      golfCourseView: adById
                        ? adById.quality.others.golfCourseView
                        : false,
                      mountainView: adById
                        ? adById.quality.others.mountainView
                        : false,
                      privateGarden: adById
                        ? adById.quality.others.privateGarden
                        : false,
                      solarium: adById ? adById.quality.others.solarium : false,
                      outdoorKitchen: adById
                        ? adById.quality.others.outdoorKitchen
                        : false,
                      carPort: adById ? adById.quality.others.carPort : false,
                      lounge: adById ? adById.quality.others.lounge : false,
                      firePlace: adById
                        ? adById.quality.others.firePlace
                        : false,
                      showKitchen: adById
                        ? adById.quality.others.showKitchen
                        : false,
                      dirtyKitchen: adById
                        ? adById.quality.others.dirtyKitchen
                        : false,
                      spa: adById ? adById.quality.others.spa : false,
                      movieTheater: adById
                        ? adById.quality.others.movieTheater
                        : false,
                      wineCellar: adById
                        ? adById.quality.others.wineCellar
                        : false,
                      laundry: adById ? adById.quality.others.laundry : false,
                      brandedDesign: adById
                        ? adById.quality.others.brandedDesign
                        : false,
                      conciergeService: adById
                        ? adById.quality.others.conciergeService
                        : false,
                      gatedCommunity: adById
                        ? adById.quality.others.gatedCommunity
                        : false,
                      panicRoom: adById
                        ? adById.quality.others.panicRoom
                        : false,
                      newConstruction: adById
                        ? adById.quality.others.newConstruction
                        : false,
                      goodConservation: adById
                        ? adById.quality.others.goodConservation
                        : false,

                      web: adById ? adById.description.web : "",
                      emailPDF: adById ? adById.description.emailPDF : "",
                      distribution: adById
                        ? adById.description.distribution
                        : "",
                    }}
                    onSubmit={(data) => {
                      setDisabledButtons(true);
                      setValidateForm(true);
                      data.showOnWeb = adShowOnWeb;
                      data.showOnWebOffMarket = adOffMarket;

                      if (id) data.id = id;
                      if (idCopy) {
                        data.idCopy = idCopy;
                      }
                      data.owner = selectedOwner;
                      data.userId = user._id;
                      data.consultant = selectedConsultant;
                      data.adType = selectedAdType;
                      data.realStatePortals = selectedRealStatePortals;
                      data.adBuildingType = selectedAdBuildingType;
                      if (data.adStatus !== adStatus) data.adStatus = adStatus;
                      data.updateSameRef = updateSurfacesBox;
                      data.adStatus = adStatus;
                      if (data.saleValue === "") data.saleValue = 0;
                      if (data.rentValue === "") data.rentValue = 0;
                      if (data.buildSurface === "") data.buildSurface = 0;
                      if (data.plotSurface === "") data.plotSurface = 0;
                      if (data.m2StorageSpace === "") data.m2StorageSpace = 0;
                      if (data.m2Terrace === "") data.m2Terrace = 0;
                      if (data.profitability === true) {
                        // Si dejó el campo de valor vacío (""), lo convertimos a 0 para que no falle el backend
                        if (data.profitabilityValue === "") {
                          data.profitabilityValue = 0;
                        }
                      }
                      // 2. Si el usuario DESMARCÓ el Checkbox (data.profitability es false)
                      else {
                        // Forzamos el valor a 0 para no guardar basura en la base de datos
                        data.profitabilityValue = 0;
                      }

                      if (residentialSelectedZones.length !== 0) {
                        data.zone = residentialSelectedZones;
                      } else if (patrimonialSelectedZones.length !== 0) {
                        data.zone = patrimonialSelectedZones;
                      } else if (othersSelectedZones.length !== 0) {
                        data.zone = othersSelectedZones;
                      } else if (costaSelectedZones.length !== 0) {
                        data.zone = costaSelectedZones;
                      } else if (rusticAndSingularSelectedZones.length !== 0) {
                        // <-- ESTE BLOQUE FALTABA
                        data.zone = rusticAndSingularSelectedZones;
                      }

                      if (data.owner && data.consultant) {
                        if (data.department) {
                          if (
                            patrimonialSelectedZones.length === 0 &&
                            residentialSelectedZones.length === 0 &&
                            othersSelectedZones.length === 0 &&
                            costaSelectedZones.length === 0 &&
                            rusticAndSingularSelectedZones.length === 0
                          ) {
                            data.zone = [];
                            alert(
                              "Debe indicar una zona dentro del departamento seleccionado.",
                            );
                            return setDisabledButtons(false);
                          }
                          if (!id || idCopy !== null) {
                            createAd(data).then((res) => {
                              alert(
                                `El anuncio ${res.adReference} ha sido creado`,
                              );
                              setDisabledButtons(false);
                              navigate(`/anuncios`);
                            });
                          } else
                            updateAd(data).then((res) => {
                              setAdById(res);
                              alert(
                                `El anuncio ${res.adReference} ha sido actualizado`,
                              );
                              setDisabledButtons(false);
                            });
                        } else {
                          alert("Debe indicar el departamento del anuncio");
                          setDisabledButtons(false);
                        }
                      } else {
                        alert(
                          "Debe completar los campos de propietario y consultor",
                        );
                        setDisabledButtons(false);
                      }
                    }}
                  >
                    {(formProps) => (
                      <Form id="AdForm">
                        <DetailsAds
                          formProps={formProps}
                          id={id ? id : ""}
                          owners={owners}
                          owner={selectedOwner}
                          setOwner={setSelectedOwner}
                          consultants={consultants}
                          consultant={selectedConsultant}
                          setConsultant={setSelectedConsultant}
                          buildingType={selectedAdBuildingType}
                          setBuildingType={setSelectedAdBuildingType}
                          selectedRealStatePortals={selectedRealStatePortals}
                          setSelectedRealStatePortals={
                            setSelectedRealStatePortals
                          }
                          adType={selectedAdType}
                          setAdType={setSelectedAdType}
                          residentials={residentials}
                          patrimonials={patrimonials}
                          others={others}
                          costa={costa}
                          rusticAndSingular={rusticAndSingular}
                          residentialSelectedZones={residentialSelectedZones}
                          setResidentialZones={setResidentialSelectedZones}
                          patrimonialSelectedZones={patrimonialSelectedZones}
                          setPatrimonialZones={setPatrimonialSelectedZones}
                          othersSelectedZones={othersSelectedZones}
                          setOthersZones={setOthersSelectedZones}
                          costaSelectedZones={costaSelectedZones}
                          setCostaZones={setCostaSelectedZones}
                          rusticAndSingularSelectedZones={
                            rusticAndSingularSelectedZones
                          }
                          setRusticAndSingularSelectedZones={
                            setRusticAndSingularSelectedZones
                          }
                          department={department}
                          setDepartment={setDepartment}
                          adStatus={adStatus}
                          setAdStatus={setAdStatus}
                          adShowOnWeb={adShowOnWeb}
                          setAdShowOnWeb={setAdShowOnWeb}
                          adOffMarket={adOffMarket}
                          setAdOffMarket={setAdOffMarket}
                          updateSufracesBox={updateSurfacesBox}
                          setUpdateSufracesBox={setUpdateSurfacesBox}
                        />
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <Spinner />
                )}
              </TabPanel>
              {id && (
                <TabPanel header="Imágenes">
                  {loader ? (
                    <ImagesAds
                      id={id}
                      setActiveIndex={setActiveIndex}
                      adById={adById ? adById : ""}
                    />
                  ) : (
                    <Spinner />
                  )}
                </TabPanel>
              )}

              {id && adById.adStatus === "Activo" && (
                <TabPanel header="Matching">
                  {loader ? (
                    <AdsMatching adById={adById} user={user} size={size} />
                  ) : (
                    <Spinner />
                  )}
                </TabPanel>
              )}
              {id &&
                adById?.changesHistory &&
                adById.changesHistory.length > 0 && (
                  <TabPanel header="Historial">
                    <AdsHistory ad={adById} />
                  </TabPanel>
                )}
            </TabView>
          )}
        </Layout>
      </div>
    </>
  );
};

export default AdForm;
